/**
 * AdminSystemTab Component
 * System health monitoring and cache management
 */
import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import {
  getSystemHealth,
  clearAllCaches,
  getCacheStats,
  getCacheKeys,
  clearMetadataCache,
  clearProductsCache,
  clearPicturesCache,
  clearMonitoringCache,
  clearAllCachesNew,
  deleteCacheKey
} from '../../services/adminService.js';
import { IconBarChart, IconChart, IconX, IconSearch, IconFileText, IconBox, IconDatabase, IconRefresh } from '../shared/Icons.jsx';
import { adminEnvironment } from '../../state/userState.js';

const STAGING_VALUES = ['staging', 'stag'];

/**
 * Human-readable environment label from the backend-authoritative
 * adminEnvironment signal (fed by GET /me/permissions), replacing the old
 * `window.location.hostname.includes('staging')` guess — which answered
 * "which server am I talking to", not "whose data am I looking at", and is
 * unreliable if the frontend is ever served from a differently-named host.
 */
function environmentLabel() {
  const env = (adminEnvironment.value.environment || '').toLowerCase();
  if (STAGING_VALUES.includes(env)) return 'Staging';
  if (env) return env.charAt(0).toUpperCase() + env.slice(1);
  return 'Unknown';
}

export function AdminSystemTab() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [clearingCache, setClearingCache] = useState(false);
  
  // New cache management state
  const [cacheStats, setCacheStats] = useState(null);
  const [cacheKeys, setCacheKeys] = useState([]);
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [showInspector, setShowInspector] = useState(false);
  const [keyPattern, setKeyPattern] = useState('*');
  const [clearingType, setClearingType] = useState(null); // 'metadata' | 'products' | 'pictures' | 'all'

  useEffect(() => {
    loadSystemHealth();
    loadCacheStats();
  }, []);

  const loadSystemHealth = async () => {
    setLoading(true);
    setError('');
    
    try {
      const data = await getSystemHealth();
      if (data && typeof data === 'object') {
        setHealth({ ...data });
      } else {
        setHealth({ status: 'healthy', version: 'v1.0.0', database: 'connected', cache: 'active' });
      }
    } catch (err) {
      console.error('[AdminSystem] Load error:', err);
      setError(err?.message || 'Failed to load system health');
      // Set default values on error
      setHealth({ status: 'unknown', version: 'N/A', database: 'unknown', cache: 'unknown' });
    } finally {
      setLoading(false);
    }
  };

  const loadCacheStats = async () => {
    setLoadingStats(true);
    try {
      const stats = await getCacheStats();
      setCacheStats(stats);
    } catch (err) {
      console.error('[AdminSystem] Cache stats error:', err);
      // Don't set error - non-critical
    } finally {
      setLoadingStats(false);
    }
  };

  const loadCacheKeys = async () => {
    setLoadingKeys(true);
    try {
      const keys = await getCacheKeys(keyPattern, 100);
      setCacheKeys(keys);
    } catch (err) {
      console.error('[AdminSystem] Cache keys error:', err);
      setError(err?.message || 'Failed to load cache keys');
    } finally {
      setLoadingKeys(false);
    }
  };

  const handleClearCache = async () => {
    if (!confirm('Clear all API caches? This will temporarily slow down API responses while the cache rebuilds.')) return;
    
    setClearingCache(true);
    setError('');
    setSuccess('');
    
    try {
      await clearAllCaches();
      setSuccess('All caches cleared successfully');
      loadCacheStats(); // Refresh stats
      if (showInspector) loadCacheKeys(); // Refresh keys if inspector is open
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('[AdminSystem] Clear cache error:', err);
      setError(err?.message || 'Failed to clear cache');
    } finally {
      setClearingCache(false);
    }
  };

  const handleClearByType = async (type) => {
    setClearingType(type);
    setError('');
    setSuccess('');
    
    try {
      let result;
      switch (type) {
        case 'metadata':
          result = await clearMetadataCache();
          break;
        case 'products':
          result = await clearProductsCache();
          break;
        case 'pictures':
          result = await clearPicturesCache();
          break;
        case 'monitoring':
          result = await clearMonitoringCache();
          break;
        case 'all':
          if (!confirm('⚠️ Clear ALL caches? This may temporarily impact performance.')) {
            setClearingType(null);
            return;
          }
          result = await clearAllCachesNew();
          break;
        default:
          throw new Error('Unknown cache type');
      }
      setSuccess(`${result.message || 'Cache cleared'} (${result.deleted_count} keys deleted)`);
      loadCacheStats(); // Refresh stats
      if (showInspector) loadCacheKeys(); // Refresh keys if inspector is open
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error(`[AdminSystem] Clear ${type} cache error:`, err);
      setError(err?.message || `Failed to clear ${type} cache`);
    } finally {
      setClearingType(null);
    }
  };

  const handleDeleteKey = async (key) => {
    if (!confirm(`Delete cache key?\n\n${key}`)) return;
    
    try {
      await deleteCacheKey(key);
      setSuccess(`Deleted: ${key}`);
      loadCacheStats();
      loadCacheKeys();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to delete key');
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTTL = (seconds) => {
    if (!seconds || seconds < 0) return 'No expiry';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    return `${Math.floor(seconds / 86400)}d`;
  };

  const getStatusColor = (status) => {
    const lowerStatus = String(status || '').toLowerCase();
    if (lowerStatus === 'healthy' || lowerStatus === 'connected' || lowerStatus === 'active' || lowerStatus === 'ok') {
      return 'admin-health-icon--success';
    } else if (lowerStatus === 'degraded' || lowerStatus === 'warning') {
      return 'admin-health-icon--warning';
    } else if (lowerStatus === 'unhealthy' || lowerStatus === 'error' || lowerStatus === 'disconnected') {
      return 'admin-health-icon--error';
    }
    return 'admin-health-icon--info';
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p>Loading system information...</p>
      </div>
    );
  }

  return (
    <div className="admin-tab-content">
      <div className="admin-section-header">
        <h2>System Health</h2>
        <button className="admin-btn admin-btn--secondary" onClick={loadSystemHealth}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
          </svg>
          Refresh
        </button>
      </div>

      {success && (
        <div className="admin-alert admin-alert--success">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <path d="M22 4L12 14.01l-3-3"/>
          </svg>
          {success}
        </div>
      )}

      {error && (
        <div className="admin-alert admin-alert--error">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M15 9l-6 6M9 9l6 6"/>
          </svg>
          {error}
        </div>
      )}

      <div className="admin-health-grid">
        <div className="admin-health-card admin-health-card--glass">
          <div className={`admin-health-icon ${getStatusColor(health?.status)}`}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <path d="M22 4L12 14.01l-3-3"/>
            </svg>
          </div>
          <div className="admin-health-content">
            <h3>API Status</h3>
            <p className="admin-health-value">{health?.status || 'Healthy'}</p>
          </div>
        </div>

        <div className="admin-health-card admin-health-card--glass">
          <div className="admin-health-icon admin-health-icon--info">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
              <line x1="7" y1="7" x2="7.01" y2="7"/>
            </svg>
          </div>
          <div className="admin-health-content">
            <h3>Version</h3>
            <p className="admin-health-value">{health?.version || 'v1.0.0'}</p>
          </div>
        </div>

        <div className="admin-health-card admin-health-card--glass">
          <div className={`admin-health-icon ${getStatusColor(health?.database)}`}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <ellipse cx="12" cy="5" rx="9" ry="3"/>
              <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
              <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
            </svg>
          </div>
          <div className="admin-health-content">
            <h3>Database</h3>
            <p className="admin-health-value">{health?.database || 'Connected'}</p>
          </div>
        </div>

        <div className="admin-health-card admin-health-card--glass">
          <div className={`admin-health-icon ${getStatusColor(health?.cache)}`}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
            </svg>
          </div>
          <div className="admin-health-content">
            <h3>Cache</h3>
            <p className="admin-health-value">{health?.cache || 'Active'}</p>
          </div>
        </div>
      </div>

      <div className="admin-actions-section">
        <div className="admin-section-header">
          <h3><IconBarChart size={16} className="admin-section-icon" /> Cache Statistics</h3>
          <button className="admin-btn admin-btn--secondary admin-btn--small" onClick={loadCacheStats} disabled={loadingStats}>
            {loadingStats ? '...' : <><IconRefresh size={14} /> Refresh</>}
          </button>
        </div>
        
        {cacheStats?.connected ? (
          <div className="admin-cache-stats">
            <div className="admin-cache-stats-grid">
              <div className="admin-cache-stat">
                <span className="admin-cache-stat-label">Memory Used</span>
                <span className="admin-cache-stat-value">{cacheStats.memory_used_human || '0B'}</span>
                <span className="admin-cache-stat-sub">Peak: {cacheStats.memory_peak_human || '0B'}</span>
              </div>
              <div className="admin-cache-stat">
                <span className="admin-cache-stat-label">Total Keys</span>
                <span className="admin-cache-stat-value">{cacheStats.total_keys || 0}</span>
                <span className="admin-cache-stat-sub">Redis {cacheStats.redis_version || 'N/A'}</span>
              </div>
              <div className="admin-cache-stat">
                <span className="admin-cache-stat-label">Uptime</span>
                <span className="admin-cache-stat-value">{formatTTL(cacheStats.uptime_seconds)}</span>
              </div>
            </div>
            
            {cacheStats.keys_by_type && (
              <div className="admin-cache-breakdown">
                <h4>Keys by Type</h4>
                <div className="admin-cache-type-list">
                  <div className="admin-cache-type-item">
                    <span>Metadata</span>
                    <span className="admin-cache-type-count">{cacheStats.keys_by_type.metadata || 0}</span>
                  </div>
                  <div className="admin-cache-type-item">
                    <span>Product List</span>
                    <span className="admin-cache-type-count">{cacheStats.keys_by_type.product_list || 0}</span>
                  </div>
                  <div className="admin-cache-type-item">
                    <span>Product Data</span>
                    <span className="admin-cache-type-count">{cacheStats.keys_by_type.product_data || 0}</span>
                  </div>
                  <div className="admin-cache-type-item">
                    <span>Pictures</span>
                    <span className="admin-cache-type-count">{cacheStats.keys_by_type.pictures || 0}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="admin-cache-stats-error">
            <p>{cacheStats?.error || 'Unable to connect to Redis'}</p>
          </div>
        )}
      </div>

      <div className="admin-actions-section">
        <h3><IconX size={16} className="admin-section-icon" /> Cache Management</h3>
        <p className="admin-actions-description">
          Clear specific cache types or all caches. Use after deployments or to fix stale data issues.
        </p>
        <div className="admin-cache-buttons">
          <button 
            className="admin-btn admin-btn--secondary" 
            onClick={() => handleClearByType('metadata')}
            disabled={clearingType !== null}
          >
            {clearingType === 'metadata' ? (
              <><div className="admin-btn-spinner"></div> Clearing...</>
            ) : (
              <><IconFileText size={14} /> Clear Metadata</>
            )}
          </button>
          <button 
            className="admin-btn admin-btn--secondary" 
            onClick={() => handleClearByType('products')}
            disabled={clearingType !== null}
          >
            {clearingType === 'products' ? (
              <><div className="admin-btn-spinner"></div> Clearing...</>
            ) : (
              <><IconBox size={14} /> Clear Products</>
            )}
          </button>
          <button 
            className="admin-btn admin-btn--secondary" 
            onClick={() => handleClearByType('pictures')}
            disabled={clearingType !== null}
          >
            {clearingType === 'pictures' ? (
              <><div className="admin-btn-spinner"></div> Clearing...</>
            ) : (
              <><IconDatabase size={14} /> Clear Pictures</>
            )}
          </button>
          <button
            className="admin-btn admin-btn--secondary"
            onClick={() => handleClearByType('monitoring')}
            disabled={clearingType !== null}
          >
            {clearingType === 'monitoring' ? (
              <><div className="admin-btn-spinner"></div> Clearing...</>
            ) : (
              <><IconChart size={14} /> Clear Monitoring</>
            )}
          </button>
          <button
            className="admin-btn admin-btn--danger"
            onClick={() => handleClearByType('all')}
            disabled={clearingType !== null}
          >
            {clearingType === 'all' ? (
              <><div className="admin-btn-spinner"></div> Clearing...</>
            ) : (
              <>Clear ALL Caches</>
            )}
          </button>
        </div>
      </div>

      <div className="admin-actions-section">
        <div className="admin-section-header">
          <h3><IconSearch size={16} className="admin-section-icon" /> Cache Inspector</h3>
          <button 
            className="admin-btn admin-btn--secondary admin-btn--small" 
            onClick={() => {
              setShowInspector(!showInspector);
              if (!showInspector) loadCacheKeys();
            }}
          >
            {showInspector ? 'Hide' : 'Show'}
          </button>
        </div>
        
        {showInspector && (
          <div className="admin-cache-inspector">
            <div className="admin-cache-search">
              <input 
                type="text" 
                value={keyPattern}
                onChange={(e) => setKeyPattern(e.target.value)}
                placeholder="Pattern (e.g., products:*)"
                className="admin-input"
              />
              <button 
                className="admin-btn admin-btn--primary admin-btn--small" 
                onClick={loadCacheKeys}
                disabled={loadingKeys}
              >
                {loadingKeys ? '...' : 'Search'}
              </button>
            </div>
            
            {cacheKeys.length > 0 ? (
              <div className="admin-cache-keys-table">
                <div className="admin-cache-keys-header">
                  <span>Key</span>
                  <span>TTL</span>
                  <span>Size</span>
                  <span></span>
                </div>
                {cacheKeys.map(item => (
                  <div key={item.key} className="admin-cache-keys-row">
                    <span className="admin-cache-key-name" title={item.key}>{item.key}</span>
                    <span className="admin-cache-key-ttl">{formatTTL(item.ttl_seconds)}</span>
                    <span className="admin-cache-key-size">{formatBytes(item.size_bytes)}</span>
                    <button 
                      className="admin-btn admin-btn--danger admin-btn--icon"
                      onClick={() => handleDeleteKey(item.key)}
                      title="Delete this key"
                    >
                      <IconX size={14} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="admin-cache-empty">No cache keys found matching pattern "{keyPattern}"</p>
            )}
          </div>
        )}
      </div>

      <div className="admin-info-section">
        <h3>System Information</h3>
        <div className="admin-info-grid">
          <div className="admin-info-item">
            <span className="admin-info-label">Environment</span>
            <span className="admin-info-value">{environmentLabel()}</span>
          </div>
          <div className="admin-info-item">
            <span className="admin-info-label">API Base URL</span>
            <span className="admin-info-value admin-info-value--mono">{window.BATEMO_API?.baseUrl || '/api/v1'}</span>
          </div>
          <div className="admin-info-item">
            <span className="admin-info-label">Frontend Version</span>
            <span className="admin-info-value">1.0.0</span>
          </div>
        </div>
      </div>
    </div>
  );
}
